<template>
  <el-dialog
    v-model="dialogVisible"
    title="选择部门"
    width="500px"
    append-to-body
    destroy-on-close
  >
    <CompactFilterBar>
      <div class="filter-bar-field-sm relative">
        <svg class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <input
          v-model="searchKeyword"
          type="text"
          placeholder="搜索部门名称"
          class="form-search-input form-control-sm w-full"
          @keyup.enter="handleSearch"
        >
      </div>

      <template #searchActions>
        <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
        <button type="button" class="btn-secondary" @click="handleReset">重置</button>
      </template>
    </CompactFilterBar>

    <div class="dept-tree max-h-80 overflow-y-auto rounded-lg border border-slate-200 p-2">
      <el-tree
        v-if="deptTree.length > 0"
        ref="treeRef"
        :data="deptTree"
        :props="{ label: 'name', children: 'children', isLeaf: 'leaf' }"
        :default-expanded-keys="expandedKeys"
        node-key="id"
        show-checkbox
        :default-checked-keys="selectedIds"
        :filter-node-method="filterNode"
        @check="handleCheck"
      >
        <template #default="{ node }">
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 00-2 0v1H8a1 1 0 000 2h1v1a1 1 0 002 0v-1h1a1 1 0 000-2h-1V9z" clip-rule="evenodd"/>
            </svg>
            <span>{{ node.label }}</span>
          </div>
        </template>
      </el-tree>
      <div v-else class="p-8 text-center text-slate-500">
        暂无部门数据
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between">
        <div class="text-sm text-slate-500">
          已选择 <span class="font-medium text-primary-600">{{ selectedIds.length }}</span> 个部门
        </div>
        <div class="flex items-center gap-2">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleConfirm">确定</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getDeptTree } from '@/api/dept'
import CompactFilterBar from '@/components/CompactFilterBar.vue'

const props = defineProps({
  modelValue: {
    type: [Array, String, Number],
    default: () => []
  },
  multiple: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = ref(false)
const loading = ref(false)
const searchKeyword = ref('')
const deptTree = ref([])
const treeRef = ref()
const expandedKeys = ref([])

const selectedIds = computed(() => {
  if (!props.modelValue) return []
  const arr = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
  return arr.filter(item => item != null).map(item => (typeof item === 'object' ? item.id : item))
})

const loadDeptTree = async () => {
  try {
    loading.value = true
    const res = await getDeptTree()
    const processTree = (nodes) => {
      return nodes.map(node => ({
        ...node,
        leaf: !node.children || node.children.length === 0,
        children: node.children ? processTree(node.children) : []
      }))
    }
    deptTree.value = processTree(res || [])
    expandedKeys.value = deptTree.value.map(d => d.id)
  } catch (error) {
    console.error('加载部门树失败:', error)
  } finally {
    loading.value = false
  }
}

const filterNode = (value, nodeData) => {
  if (!value) return true
  return nodeData.name?.includes(value) || false
}

const handleSearch = () => {
  if (treeRef.value) {
    treeRef.value.filter(searchKeyword.value)
  }
}

const handleReset = () => {
  searchKeyword.value = ''
  handleSearch()
}

const handleCheck = () => {
  if (treeRef.value) {
    const checkedNodes = treeRef.value.getCheckedNodes()
    const values = checkedNodes.map(node => ({ id: node.id, name: node.name }))
    emit('update:modelValue', props.multiple ? values : values[0] || null)
  }
}

const handleConfirm = () => {
  if (treeRef.value) {
    const checkedNodes = treeRef.value.getCheckedNodes()
    const values = checkedNodes.map(node => ({ id: node.id, name: node.name }))
    emit('update:modelValue', props.multiple ? values : values[0] || null)
  }
  dialogVisible.value = false
}

watch(dialogVisible, (val) => {
  if (val) {
    loadDeptTree()
  }
})

const open = () => {
  dialogVisible.value = true
}

defineExpose({ open })
</script>
